import { apiService, ApiResponse } from './api';
import { 
  Exercise, 
  ExerciseRequest, 
  MemberExercise, 
  MemberExerciseRequest,
  ExerciseFilters 
} from '../types/Workout';

class ExerciseService {
  // ==================== EXERCISE LIBRARY MANAGEMENT ====================
  
  /** Get all exercises */
  async getAllExercises(): Promise<ApiResponse<Exercise[]>> {
    return apiService.get<Exercise[]>('/api/exercises');
  }

  /** Get exercise by ID */
  async getExercise(exerciseId: number): Promise<ApiResponse<Exercise>> {
    return apiService.get<Exercise>(`/api/exercises/${exerciseId}`);
  }

  /** Search exercises by name */
  async searchExercises(name: string): Promise<ApiResponse<Exercise[]>> {
    return apiService.get<Exercise[]>(`/api/exercises/search?name=${encodeURIComponent(name)}`);
  }

  /** Get exercises by equipment */
  async getExercisesByEquipment(equipment: string): Promise<ApiResponse<Exercise[]>> {
    return apiService.get<Exercise[]>(`/api/exercises/equipment/${encodeURIComponent(equipment)}`);
  }

  /** Get exercises by muscle group */
  async getExercisesByMuscleGroup(muscleGroup: string): Promise<ApiResponse<Exercise[]>> {
    return apiService.get<Exercise[]>(`/api/exercises/muscle-group/${encodeURIComponent(muscleGroup)}`);
  }

  /** Advanced search with multiple criteria */
  async searchExercisesByCriteria(filters: ExerciseFilters): Promise<ApiResponse<Exercise[]>> {
    const params = new URLSearchParams();
    if (filters.name) params.append('name', filters.name);
    if (filters.equipment) params.append('equipment', filters.equipment);
    
    return apiService.get<Exercise[]>(`/api/exercises/search/advanced?${params.toString()}`);
  }

  /** Create new exercise (Admin/Trainer only) */
  async createExercise(request: ExerciseRequest): Promise<ApiResponse<Exercise>> {
    return apiService.post<Exercise>('/api/exercises', request);
  }

  /** Update exercise (Admin/Trainer only) */
  async updateExercise(exerciseId: number, request: ExerciseRequest): Promise<ApiResponse<Exercise>> {
    return apiService.put<Exercise>(`/api/exercises/${exerciseId}`, request);
  }

  /** Delete exercise (Admin/Trainer only) */
  async deleteExercise(exerciseId: number): Promise<ApiResponse<{message: string}>> {
    return apiService.delete<{message: string}>(`/api/exercises/${exerciseId}`);
  }

  // ==================== WORKOUT EXERCISE MANAGEMENT ====================

  /** Add exercise to workout */
  async addExerciseToWorkout(request: MemberExerciseRequest): Promise<ApiResponse<MemberExercise>> {
    return apiService.post<MemberExercise>('/api/workout-exercises', request);
  }

  /** Update exercise in workout (sets, order, etc.) */
  async updateExerciseInWorkout(memberExerciseId: number, request: MemberExerciseRequest): Promise<ApiResponse<MemberExercise>> {
    return apiService.put<MemberExercise>(`/api/workout-exercises/${memberExerciseId}`, request);
  }

  /** Get all exercises for a workout */
  async getWorkoutExercises(workoutId: number): Promise<ApiResponse<MemberExercise[]>> {
    return apiService.get<MemberExercise[]>(`/api/workout-exercises/workout/${workoutId}`);
  }

  /** Get specific exercise in workout */
  async getWorkoutExercise(workoutId: number, exerciseId: number): Promise<ApiResponse<MemberExercise>> {
    return apiService.get<MemberExercise>(`/api/workout-exercises/workout/${workoutId}/exercise/${exerciseId}`);
  }

  /** Get member exercise by ID */
  async getMemberExercise(memberExerciseId: number): Promise<ApiResponse<MemberExercise>> {
    return apiService.get<MemberExercise>(`/api/workout-exercises/${memberExerciseId}`);
  }

  /** Remove exercise from workout */
  async removeExerciseFromWorkout(workoutId: number, exerciseId: number): Promise<ApiResponse<{message: string}>> {
    return apiService.delete<{message: string}>(`/api/workout-exercises/workout/${workoutId}/exercise/${exerciseId}`);
  }

  /** Remove member exercise by ID */
  async removeMemberExercise(memberExerciseId: number): Promise<ApiResponse<{message: string}>> {
    return apiService.delete<{message: string}>(`/api/workout-exercises/${memberExerciseId}`);
  }

  /** Reorder exercises in workout */
  async reorderExercises(workoutId: number, memberExerciseIds: number[]): Promise<ApiResponse<{message: string}>> {
    return apiService.put<{message: string}>(`/api/workout-exercises/workout/${workoutId}/reorder`, memberExerciseIds);
  }

  /** Get all exercises for a member across all workouts */
  async getMemberAllExercises(memberId: number): Promise<ApiResponse<MemberExercise[]>> {
    return apiService.get<MemberExercise[]>(`/api/workout-exercises/member/${memberId}`);
  }

  /** Get exercise count for a workout */
  async getWorkoutExerciseCount(workoutId: number): Promise<ApiResponse<{count: number}>> {
    return apiService.get<{count: number}>(`/api/workout-exercises/workout/${workoutId}/count`);
  }

  // ==================== UTILITY METHODS ====================

  /** Get unique muscle groups from exercises */
  getMuscleGroupsFromExercises(exercises: Exercise[]): string[] {
    const muscleGroupsSet = new Set<string>();
    
    exercises.forEach(exercise => {
      if (exercise.muscleGroups) {
        exercise.muscleGroups.split(',').forEach(group => {
          muscleGroupsSet.add(group.trim());
        });
      }
    });
    
    return Array.from(muscleGroupsSet).sort();
  }

  /** Get unique equipment from exercises */
  getEquipmentFromExercises(exercises: Exercise[]): string[] {
    const equipmentSet = new Set<string>();
    
    exercises.forEach(exercise => {
      if (exercise.equipment) {
        equipmentSet.add(exercise.equipment.trim());
      }
    });
    
    return Array.from(equipmentSet).filter(eq => eq !== 'None').sort();
  }
}

export const exerciseService = new ExerciseService(); 